import React, { useState, useRef } from 'react';
import client from '../../api/client';

export default function ExcelUploadModal({ title, onUpload, onClose, sampleColumns = [], templateEntity }) {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const inputRef = useRef();

  const handleUpload = async () => {
    if (!file) { setError('Please select a file.'); return; }
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const res = await onUpload(file);
      setResult(res);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'Upload failed.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadCSV = () => {
    const csv = sampleColumns.join(',') + '\n';
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sample_${title.toLowerCase().replace(/\s+/g, '_')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDownloadSample = async () => {
    if (templateEntity) {
      try {
        const resp = await client.get(`/admin/templates/${templateEntity}/`, { responseType: 'blob' });
        const url = window.URL.createObjectURL(new Blob([resp.data]));
        const a = document.createElement('a');
        a.href = url;
        a.download = `template_${templateEntity}.xlsx`;
        a.click();
        window.URL.revokeObjectURL(url);
      } catch {
        // fallback to CSV
        handleDownloadCSV();
      }
    } else {
      handleDownloadCSV();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-base font-semibold text-gray-800">Upload Excel â€” {title}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6 space-y-4">
          {sampleColumns.length > 0 && (
            <div className="bg-purple-50 rounded-xl p-3 text-xs text-purple-700">
              <p className="font-semibold mb-1">Expected columns:</p>
              <p className="font-mono">{sampleColumns.join(', ')}</p>
            </div>
          )}

          <div
            className="border-2 border-dashed border-gray-200 rounded-xl p-6 text-center cursor-pointer hover:border-purple-300 transition-colors"
            onClick={() => inputRef.current?.click()}
          >
            <svg className="w-10 h-10 mx-auto text-gray-300 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
            </svg>
            {file
              ? <p className="text-sm text-gray-700 font-medium">{file.name}</p>
              : <p className="text-sm text-gray-400">Click to select .xlsx or .xls file</p>
            }
            <input
              ref={inputRef}
              type="file"
              accept=".xlsx,.xls"
              className="hidden"
              onChange={e => { setFile(e.target.files[0] || null); setResult(null); setError(''); }}
            />
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3">{error}</div>
          )}

          {result && (
            <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3 space-y-1">
              <p className="text-sm font-semibold text-green-800">Upload Complete</p>
              <div className="flex gap-4 text-sm">
                <span className="text-green-700">Created: <strong>{result.created ?? 0}</strong></span>
                <span className="text-blue-700">Updated: <strong>{result.updated ?? 0}</strong></span>
                <span className="text-red-600">Errors: <strong>{result.errors ?? 0}</strong></span>
              </div>
              {result.error_list?.length > 0 && (
                <ul className="mt-2 space-y-0.5 max-h-32 overflow-y-auto">
                  {result.error_list.map((e, i) => (
                    <li key={i} className="text-xs text-red-600">â€¢ {e}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        <div className="px-6 pb-5 flex items-center justify-between gap-3">
          <button
            onClick={handleDownloadSample}
            className="text-sm text-purple-600 hover:text-purple-800 font-medium flex items-center gap-1.5"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            Download Sample
          </button>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50">
              Cancel
            </button>
            <button
              onClick={handleUpload}
              disabled={loading || !file}
              className="px-4 py-2 text-sm rounded-xl bg-[#C1278D] hover:bg-[#a01f75] text-white font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Uploadingâ€¦' : 'Upload'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

